// src/hoc/withModuleSetup.js
import React, { useEffect, useState, useMemo } from 'react';
import useZustand from 'hooks/useZustand';
import { useLocation } from 'react-router-dom';
import PageProvider from 'components/core/PageProvider';

let currentModuleId;
let lastPathname;

const withModuleSetup = (WrappedComponent, pageName, moduleConfig, noComponent) => {
  const ComponentWithSetup = React.forwardRef((props, ref) => {
    const setModuleStore = useZustand('module', 'setModule');
    const storedSubscription = useZustand('auth', 'subscription');

    // FIX: Use granular selector to prevent unnecessary re-renders on language change
    const storedAppActivated = useZustand('app', 'appActivated');

    const [isReady, setIsReady] = useState(false);

    // const parsedUrl = useParseUrl(true);
    const location = useLocation();

    useEffect(() => {
      const setup = async () => {
        const pathnameChanged = lastPathname !== location.pathname;

        // Reset currentModuleId when the pathname changes
        if (pathnameChanged) {
          currentModuleId = null;
          lastPathname = location.pathname;
        }

        if (
          (storedSubscription?._id || !storedAppActivated) &&
          (!moduleConfig || currentModuleId !== moduleConfig?.id)
        ) {
          currentModuleId = moduleConfig?.id;

          await setModuleStore({
            moduleConfig,
            pageName,
            noComponent,
          });
        }

        setIsReady(true);
      };

      setup().catch();
    }, [storedSubscription?._id, storedAppActivated, location.pathname]);

    // Detect page layout from module config to decide if PageProvider is needed
    const pageLayout = moduleConfig?.pages?.[pageName]?.layout;

    // FIX: Memoize component to prevent recreation on parent re-renders
    // Wrap with PageProvider for 'page' layout so page components can access usePageContext()
    const MemoizedComponent = useMemo(() => {
      const inner = <WrappedComponent {...props} ref={ref} />;
      if (pageLayout === 'page') {
        return <PageProvider>{inner}</PageProvider>;
      }
      return inner;
      // pageLayout is a stable string from config — does not cause extra re-renders
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props, ref, pageLayout]);

    if (!isReady) return null;

    return MemoizedComponent;
  });

  ComponentWithSetup.displayName = `withModuleSetup(${WrappedComponent.displayName || WrappedComponent.name})`;

  return ComponentWithSetup;
};

export default withModuleSetup;
